'use client';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Div, ListItem, Media, Popper, PopperContent, PopperHandler, Text, TextField } from '@pezeshk-book/ui-kit';
import { AutoCompleteProps } from './auto-complete.props';
import { useDebouncedCallback } from 'use-debounce';
import classNames from '@utils/helpers/class-names';
import SearchIcon from '@icons-components/search-icon';
import { useParams } from 'next/navigation';
import getDictionary from '@dictionaries-client';
import { LocaleTypes } from '@dictionaries-types';

const AutoComplete = ({ placeholder, optionsList, text, id, onSelect, loading, onChange, value, button = false, onClick, returnObject = false, className }: AutoCompleteProps) => {
  const [showList, setShowList] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<Array<any>>([]);
  const [inputText, setInputText] = useState<string>(value || '');

  const { lang } = useParams<{lang:LocaleTypes}>();
  const { modules } = getDictionary(lang);

  useEffect(() => {
    setInputText(value || '');
  }, [value]);

  useEffect(() => {
    if (optionsList && optionsList.length) {
      setSearchResult(optionsList.slice(0, 10));
    }
  }, [optionsList]);

  const handlePopper = () => {
    setShowList(!showList);
  };

  const handleSelectItem = ({ item }: { item: any }) => {
    onSelect(returnObject ? item : item[id]);
    setInputText(item[text]);
    setShowList(false);
  };

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    await setInputText(inputValue);
    if (onChange) {
      onChange(e);
    }
    await searchFunction(inputValue);
    setShowList(true);
  };

  const searchFunction = useDebouncedCallback((inputValue: string) => {
    const search = optionsList.filter((value) => {
      if (value[text].includes(inputValue)) {
        return value;
      }
    });
    setSearchResult(search);
  }, 500);

  return (
    <Div className={'flex-col relative w-full items-center justify-center'}>
      <Popper position={'bottom'} showPopper={showList} handlePopper={handlePopper} className={'w-full'}>
        <PopperHandler>
          <TextField
            color={'primary'}
            className={'w-full'}
            inputClassName={classNames(className, `${button ? 'md:pl-36 sm:pl-14 pl-14' : 'pl-12'}`)}
            placeholder={placeholder}
            value={inputText}
            size={button ? 'huge' : 'medium'}
            onChange={handleInputChange}
            endAdornmentClassName={'md:!w-32 sm:w-12 !w-10'}
            endAdornment={<>
              {button ? (
                <Div>
                  <Media greaterThan={'sm'}>
                    <Button
                      loading={loading}
                      disabled={loading}
                      startAdornment={<SearchIcon/>}
                      onClick={onClick}
                      size={'medium'}
                      ariaLabel={modules.auto_complete.search_button}
                      className={'w-32 whitespace-nowrap'}>
                      {modules.auto_complete.search_button}
                    </Button>
                  </Media>
                  <Media lessThan={'md'}>
                    <Button loading={loading} disabled={loading} ariaLabel={modules.auto_complete.search_button} size={'medium'} onClick={onClick} shape="square" className={'!pl-0 !pr-0'}>
                      <Div className={'relative h-6 w-6 ml-0 md:w-8 md:h-8 md:ml-4 items-center'}>
                        <SearchIcon/>
                      </Div>
                    </Button>
                  </Media>
                </Div>
              ) : null}
            </>}
          />
        </PopperHandler>
        <PopperContent className={'w-full'}>
          <Div className={'bg-white shadow-lg rounded py-6 px-4'}>
            {searchResult && searchResult.length ? (
              <Div className={'flex-col w-full gap-2 h-52 overflow-y-scroll'}>
                {searchResult.map((item, index) => (
                  <ListItem className={'cursor-pointer hover:bg-primary hover:text-white rounded text-right'} onClick={() => handleSelectItem({ item: item })} key={`result_${index}`}>
                    {item[text]}
                  </ListItem>
                ))}
              </Div>
            ) : (
              <Text>
                {modules.auto_complete.empty}
              </Text>
            )}
          </Div>
        </PopperContent>
      </Popper>
    </Div>
  );
};

export default AutoComplete;
